import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Chart, ChartConfiguration, ChartDataset } from 'chart.js/auto';
import { ReportingService } from 'src/app/reporting/shared/services/reporting.service';
import {
  WeatherDataTypeEnum,
  resolveWeatherDataType,
  weatherChartGroups,
  weatherDataTypeDisplays,
  weatherUnknownTypeChartColor
} from 'src/app/device/shared/enums/weatherData-type.enum';
import { WeatherGraphData, WeatherValuesPerType } from '../../interfaces/weather-graph-data.interface';

/** One chart of the dialog with the data prepared for it. */
interface WeatherChartView {
  title: string;
  axisLabel: string;
  datasets: ChartDataset<'line'>[];
  /** Unit and precision per dataset index, used for the tooltip value. */
  valueFormats: { decimalPlaces: number, unit: string }[];
}

@Component({
  selector: 'avs-weatherData-chart',
  templateUrl: './weatherData-chart.component.html',
  styleUrls: ['./weatherData-chart.component.css']
})
export class WeatherDataChartComponent implements OnInit, OnDestroy {

  @ViewChildren('weatherCanvas') private canvasRefs!: QueryList<ElementRef<HTMLCanvasElement>>;

  public samId: number;
  public deviceName: string = "";

  public now = new Date();
  public nowAsISOString: string;
  public twoDaysAgoAsString: string;

  public fromDate: number = 2;
  public toDate: number = 0;
  public dataAge: number = 0;
  public fontSize: number = 10;
  /** false as long as the API delivered no measurements for the period. */
  public hasData: boolean = true;
  /** Charts to be drawn — one per unit occurring in the data. */
  public chartViews: WeatherChartView[] = [];
  /** Measurement times of the last response, used for the tooltip title. */
  private timeLabels: string[] = [];
  private charts: Chart[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private reportingService: ReportingService,
    private changeDetector: ChangeDetectorRef,
    private dialogRef: MatDialogRef<WeatherDataChartComponent>,
  ) {
    // The dialog is opened with { data: <schema data> }, older callers pass the schema data directly.
    const schemaData = this.data?.data ?? this.data;

    const lastDataTime = schemaData?.schemaInfo?.weather?.data?.dataDateTime;
    const lastData = lastDataTime ? new Date(lastDataTime) : undefined;
    if (lastData && !Number.isNaN(lastData.getTime())) {
      const diff = new Date().getTime() - lastData.getTime();
      this.dataAge = diff / (1000 * 60 * 60 * 24);
    }

    if (this.dataAge > 1) {
      this.toDate = Math.round(this.dataAge);
      this.fromDate = Math.round(this.dataAge) + 2;
    }

    this.samId = schemaData.routeStation.samId;
    this.deviceName = schemaData.routeStation.name;

    // Initial period from the input fields, so that the first request already
    // covers the period displayed there.
    this.nowAsISOString = this.now.toISOString();
    this.twoDaysAgoAsString = this.now.toISOString();
    this.setFromDate(this.fromDate);
    this.setToDate(this.toDate);
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  ngOnInit(): void {
    this.getData();
  }

  private getData(): void {
    this.destroyCharts();

    this.reportingService.getWeatherDataGraphics(
      { utcFrom: this.twoDaysAgoAsString, utcTo: this.nowAsISOString, samId: this.samId }).subscribe({
        next: (data: WeatherGraphData) => this.createCharts(data),
        // Error messages are already shown by the HttpService.
        error: () => {
          this.chartViews = [];
          this.hasData = false;
        }
      });
  }

  public removeData(): void {
    this.destroyCharts();
    this.getData();
  }

  private destroyCharts(): void {
    this.charts.forEach(chart => chart.destroy());
    this.charts = [];
  }

  private createCharts(data: WeatherGraphData): void {
    this.timeLabels = data?.dataDateTimes ?? [];
    const series = this.timeLabels.length > 0 ? data?.dataPerType ?? [] : [];

    this.chartViews = this.createChartViews(series);
    this.hasData = this.chartViews.length > 0;

    this.destroyCharts();
    // The canvases of the *ngFor have to exist before the charts are drawn.
    this.changeDetector.detectChanges();

    this.canvasRefs.forEach((canvasRef, index) => {
      const view = this.chartViews[index];
      if (view) { this.charts.push(new Chart(canvasRef.nativeElement, this.createChartConfig(view))); }
    });
  }

  /**
   * Distributes the delivered data types onto one chart per unit — values of
   * different units cannot share a y-axis. Charts whose data types the API did
   * not deliver are omitted.
   */
  private createChartViews(series: WeatherValuesPerType[]): WeatherChartView[] {
    const known = new Map<WeatherDataTypeEnum, WeatherValuesPerType>();
    const unknown: WeatherValuesPerType[] = [];

    series.forEach(entry => {
      const type = resolveWeatherDataType(entry.valueType);
      if (type !== undefined) { known.set(type, entry); }
      else { unknown.push(entry); }
    });

    const views: WeatherChartView[] = [];

    weatherChartGroups.forEach(group => {
      const view: WeatherChartView = {
        title: group.title, axisLabel: group.axisLabel, datasets: [], valueFormats: []
      };

      group.types.forEach(type => {
        const entry = known.get(type);
        if (!entry) { return; }

        const display = weatherDataTypeDisplays[type];
        this.addDataset(view, display.label, display.chartColor, entry.values,
          display.decimalPlaces, display.unit);
      });

      if (view.datasets.length > 0) { views.push(view); }
    });

    // Data types unknown here: own chart, since their unit is unknown too.
    if (unknown.length > 0) {
      const view: WeatherChartView = {
        title: 'Weitere Datentypen', axisLabel: 'Wert', datasets: [], valueFormats: []
      };
      unknown.forEach(entry => this.addDataset(view, `DE-Typ ${entry.valueType}`,
        weatherUnknownTypeChartColor, entry.values, 2, ''));
      views.push(view);
    }

    return views;
  }

  private createChartConfig(view: WeatherChartView): ChartConfiguration<'line'> {
    return {
      type: 'line',
      data: {
        labels: this.timeLabels,
        datasets: view.datasets
      },
      options: {
        aspectRatio: 5.0,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: { display: true },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              maxTicksLimit: 20
            }
          },
          y: {
            title: { display: true, text: view.axisLabel }
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                return this.formatDateTime(this.timeLabels[index]);
              },
              label: (item: any) => this.formatTooltipLabel(view, item)
            }
          },
          legend: {
            labels: {
              font: {
                size: this.fontSize
              }
            }
          }
        }
      }
    };
  }

  /**
   * Adds one measurement series to a chart. The unit is not part of the label:
   * it is already on the y-axis of the chart.
   */
  private addDataset(
    view: WeatherChartView,
    label: string,
    color: string,
    values: (number | null)[],
    decimalPlaces: number,
    unit: string): void {

    view.valueFormats.push({ decimalPlaces, unit });

    view.datasets.push({
      label,
      data: values,
      borderColor: color,
      backgroundColor: color,
      fill: false,
      tension: 0.1,
      borderWidth: 1,
      pointRadius: 2,
      pointHoverRadius: 4,
      pointStyle: 'rectRounded'
    });
  }

  /** Tooltip value with the unit and precision of the data type of the dataset. */
  private formatTooltipLabel(view: WeatherChartView, item: any): string {
    const label = item.dataset?.label ?? '';
    const value = item.parsed?.y;
    if (value == null) { return `${label}: -`; }

    const format = view.valueFormats[item.datasetIndex];
    return `${label}: ${(value.toFixed(format?.decimalPlaces ?? 2) + ' ' + (format?.unit ?? '')).trim()}`;
  }

  private formatDateTime(isoString: string): string {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month} ${hours}:${minutes}`;
  }

  public setFromDate(date: number): void {
    this.fromDate = date;
    const d = new Date();
    d.setDate(d.getDate() - this.fromDate);
    this.twoDaysAgoAsString = d.toISOString();
  }

  public setToDate(date: number): void {
    this.toDate = date;
    const d = new Date();
    d.setDate(d.getDate() - this.toDate);
    this.nowAsISOString = d.toISOString();
  }

  public onCloseDialogClicked(): void {
    this.dialogRef.close();
  }

  public exportChart(): void {
    var allCanvas: HTMLCanvasElement[] = [];
    var height: number = 0;
    var width: number = 0;
    this.canvasRefs.forEach(canvasRef => {
      let canvas = canvasRef.nativeElement;
      height += (canvas.height+20);
      width = Math.max(width, canvas.width);
      allCanvas.push(canvas);
    });

    if (allCanvas.length === 0) { return; }

    const combinedCanvas = document.createElement('canvas');
    combinedCanvas.width = width;
    combinedCanvas.height = height - 20;

    const ctx = combinedCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);
      var x: number = 0;
      var currentY: number = 0;
      allCanvas.forEach(c=> {
        ctx.drawImage(c, x, currentY);
        currentY += (c.height+20);
      });

    let dataURL = combinedCanvas.toDataURL('image/png');
    let link = document.createElement('a');
    link.href = dataURL;
    link.download = `Wetterdaten-verlauf-${this.samId}-${new Date().toISOString().split('T')[0]}.png`;
    link.click();
    }
  }
}
